import { useState } from "react";
import type { SubmissionMatch, SubmissionResponse } from "@guestbook/shared";
import { Badge } from "@/shared/components/ui/Badge";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { SelectInput } from "@/shared/components/ui/SelectInput";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/AlertDialog";
import {
  useApproveSubmission,
  useRejectSubmission,
  useSubmissions,
} from "@/shared/lib/submissions";
import { formatAddress } from "@/shared/lib/address";

/**
 * The "review submissions" half of the Collect Contacts page. Lists incoming
 * submissions; each can be added as a new contact, merged into a matched
 * existing contact, or deleted.
 */
export function SubmissionReview() {
  const [showResolved, setShowResolved] = useState(false);
  const submissions = useSubmissions({ status: showResolved ? undefined : "pending" });
  const items = submissions.data?.items ?? [];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="type-heading-sm text-text">Submissions</h2>
          <p className="type-body-sm text-text-muted">
            Review what people have shared before it joins your contacts.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={() => setShowResolved((v) => !v)}
        >
          {showResolved ? "Pending only" : "Show all"}
        </Button>
      </div>

      {submissions.isLoading ? (
        <p className="type-body-base text-text-muted">Loading…</p>
      ) : submissions.isError ? (
        <p className="type-body-sm text-danger-strong" role="alert">
          Couldn't load submissions: {submissions.error.message}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          title={showResolved ? "No submissions yet" : "Nothing to review"}
          description={
            showResolved
              ? "Share your link and submissions will show up here for review."
              : "You're all caught up. New submissions will appear here for review."
          }
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((submission) => (
            <li key={submission.id}>
              <SubmissionCard submission={submission} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function matchLabel(match: SubmissionMatch): string {
  return match.name || match.email || "Unnamed contact";
}

function SubmissionCard({ submission }: { submission: SubmissionResponse }) {
  const approve = useApproveSubmission();
  const reject = useRejectSubmission();
  const { payload, matches } = submission;
  const pending = submission.status === "pending";
  const [mergeTarget, setMergeTarget] = useState(matches[0]?.contactId ?? "");
  const busy = approve.isPending || reject.isPending;

  const detailRows: Array<[string, string]> = [];
  if (payload.email) detailRows.push(["Email", payload.email]);
  if (payload.phone) detailRows.push(["Phone", payload.phone]);
  const address = formatAddress(payload.address);
  if (address) detailRows.push(["Address", address]);
  if (payload.notes) detailRows.push(["Notes", payload.notes]);

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border-muted glass-surface-subtle p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="type-heading-sm text-text">{payload.name || "No name provided"}</p>
        {!pending ? (
          <Badge variant="muted">{submission.status}</Badge>
        ) : null}
      </div>

      {detailRows.length > 0 ? (
        <dl className="flex flex-col gap-1">
          {detailRows.map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <dt className="type-label-sm tracking-wider uppercase text-text-muted w-20 shrink-0">
                {label}
              </dt>
              <dd className="type-body-sm text-text break-words">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="type-body-sm text-text-muted">No details provided.</p>
      )}

      {pending ? (
        <div className="flex flex-col gap-3">
          {matches.length > 0 ? (
            <div className="flex flex-col gap-2 rounded-lg bg-surface-muted/40 p-3">
              <p className="type-label-sm tracking-wider uppercase text-text-muted">
                Possible existing contact
              </p>
              {matches.length === 1 ? (
                <p className="type-body-sm text-text">
                  Matches{" "}
                  <span className="text-text">{matchLabel(matches[0]!)}</span> by{" "}
                  {matches[0]!.reason}.
                </p>
              ) : (
                <SelectInput
                  aria-label="Choose the contact to merge into"
                  options={matches.map((match) => ({
                    value: match.contactId,
                    label: `${matchLabel(match)} (by ${match.reason})`,
                  }))}
                  value={mergeTarget}
                  onValueChange={setMergeTarget}
                />
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {matches.length > 0 ? (
              <Button
                size="sm"
                onClick={() => approve.mutate({ id: submission.id, contactId: mergeTarget })}
                disabled={busy || !mergeTarget}
              >
                Merge
              </Button>
            ) : null}
            <Button
              size="sm"
              variant={matches.length > 0 ? "secondary" : "default"}
              onClick={() => approve.mutate({ id: submission.id })}
              disabled={busy}
            >
              {matches.length > 0 ? "Add as new" : "Add contact"}
            </Button>
            <DeleteSubmissionButton
              disabled={busy}
              pending={reject.isPending}
              onConfirm={() => reject.mutate(submission.id)}
            />
          </div>

          {approve.isError ? (
            <p className="type-body-sm text-danger-strong" role="alert">
              {approve.error.message}
            </p>
          ) : null}
          {reject.isError ? (
            <p className="type-body-sm text-danger-strong" role="alert">
              {reject.error.message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DeleteSubmissionButton({
  disabled,
  pending,
  onConfirm,
}: {
  disabled: boolean;
  pending: boolean;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="ghost-danger" disabled={disabled}>
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
          <AlertDialogDescription>
            It will be removed from your review list. No contact is created.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button variant="destructive" onClick={onConfirm} disabled={pending}>
              {pending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
